import { createLogger } from './bf-lp1-logger.js';

const logger = createLogger(false, 'CustomElements', 'info');

class BaseCarousel extends HTMLElement {
    constructor() {
        super();
        this.isMobile = window.innerWidth < 768;
        this.currentIndex = 0;
        this.slides = [];
        this.track = null;
        this.prevButton = null;
        this.nextButton = null;
    }

    getSlidesPerView() {
        const mobileSlides = this.getAttribute('mobile-slides') || 1;
        const desktopSlides = this.getAttribute('desktop-slides') || 2;
        const isMobile = window.innerWidth < 768;
        const slidesPerView = isMobile ? parseInt(mobileSlides, 10) : parseInt(desktopSlides, 10);
        
        if (this.logger) {
            this.logger.debug('Getting slides per view:', {
                isMobile,
                mobileSlides,
                desktopSlides,
                slidesPerView
            });
        }
        
        return slidesPerView;
    }

    updateSlideWidths() {
        if (!this.track || !this.slides || this.slides.length === 0) return;
        
        const slidesPerView = this.getSlidesPerView();
        const slideWidth = 100 / slidesPerView;
        
        this.slides.forEach(slide => {
            slide.style.flex = `0 0 ${slideWidth}%`;
            slide.style.width = `${slideWidth}%`;
            slide.style.display = 'flex';
        });
        
        if (this.logger) {
            this.logger.debug('Updated slide widths', { slidesPerView, slideWidth });
        }
    }

    connectedCallback() {
        this.logger.info('Carousel connected');
        this.initialize();
        
        this.setupResizeListener();

        // Handle solutionsShown event
        this.handleSolutionsShown = () => {
            if (this.logger) this.logger.debug('Solutions shown, reinitializing video event listeners');
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                this.querySelectorAll('video[data-video-autoplay]').forEach(video => {
                    this.observeVideoVisibility(video);
                });
            }, 100);
        };
        document.addEventListener('solutionsShown', this.handleSolutionsShown);
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
        if (this.prevButton) {
            this.prevButton.removeEventListener('click', this.handlePrevClick);
        }
        if (this.nextButton) {
            this.nextButton.removeEventListener('click', this.handleNextClick);
        }
        if (this.handleSolutionsShown) {
            document.removeEventListener('solutionsShown', this.handleSolutionsShown);
        }
    }

    handleResize = () => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 768;
        
        if (this.logger) {
            this.logger.debug('Resize event:', {
                wasMobile,
                isMobile: this.isMobile,
                windowWidth: window.innerWidth,
            });
        }

        this.updateSlideWidths();
        this.updatePosition();
        this.updateArrowVisibility();

        if (wasMobile !== this.isMobile) {
            if (this.logger) {
                this.logger.info('Viewport changed between mobile/desktop');
            }
        }
    }

    setupResizeListener() {
        window.addEventListener('resize', this.handleResize);
    }

    initialize() {
        if (this.logger) this.logger.info('Initializing BaseCarousel DOM elements');
        this.track = this.querySelector('[data-carousel-track]');
        this.prevButton = this.querySelector('button[aria-label="Previous"]');
        this.nextButton = this.querySelector('button[aria-label="Next"]');
        
        if (!this.track) {
            if (this.logger) this.logger.error('Carousel track not found');
            return;
        }
        
        this.slides = Array.from(this.track.children);
        if (this.logger) this.logger.debug(`Found ${this.slides.length} slides`);

        this.updateSlideWidths();
        this.setupEventListeners();
        this.updatePosition();
        this.updateArrowVisibility();
    }

    handlePrevClick = () => this.prevSlide();
    handleNextClick = () => this.nextSlide();

    setupEventListeners() {
        if (this.logger) this.logger.debug('Setting up event listeners');
        if (this.prevButton) {
            this.prevButton.removeEventListener('click', this.handlePrevClick);
            this.prevButton.addEventListener('click', this.handlePrevClick);
        }
        
        if (this.nextButton) {
            this.nextButton.removeEventListener('click', this.handleNextClick);
            this.nextButton.addEventListener('click', this.handleNextClick);
        }
        
        this.querySelectorAll('video[data-video-autoplay]').forEach(video => {
            this.observeVideoVisibility(video);
        });
    }
    
    observeVideoVisibility(video) {
        const videoContainer = video.closest('[data-video-container]');
        if (!videoContainer) return;

        const containerId = videoContainer.getAttribute('data-video-container');
        if (!containerId) return;

        // Remove any existing observers and event listeners for this container
        if (window.videoObservers && window.videoObservers[containerId]) {
            window.videoObservers[containerId].disconnect();
        }
        if (window.videoEventListeners && window.videoEventListeners[containerId]) {
            window.videoEventListeners[containerId].forEach(listener => {
                listener.element.removeEventListener(listener.type, listener.handler);
            });
        }

        // Reset video state
        video.pause();
        video.currentTime = 0;
        video.muted = true; // Always muted for carousel videos

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(err => {
                            if (this.logger) this.logger.debug('Autoplay failed:', err);
                            // If autoplay fails, try again on next intersection
                            observer.unobserve(videoContainer);
                            setTimeout(() => observer.observe(videoContainer), 100);
                        });
                    }
                } else {
                    video.pause();
                }
            });
        }, {
            threshold: [0.1, 0.5, 0.9],
            rootMargin: '50px'
        });

        observer.observe(videoContainer);

        // Store observer for this container
        if (!window.videoObservers) window.videoObservers = {};
        window.videoObservers[containerId] = observer;

        // Create click handler
        const clickHandler = function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            if (video.paused) {
                video.play().catch(err => {
                    if (this.logger) this.logger.debug('Play failed:', err);
                });
            } else {
                video.pause();
            }
        };

        // Create touch handler
        const touchHandler = function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            if (video.paused) {
                video.play().catch(err => {
                    if (this.logger) this.logger.debug('Touch play failed:', err);
                });
            }
        };

        // Add event listeners to both container and video
        videoContainer.addEventListener('click', clickHandler);
        video.addEventListener('click', clickHandler);
        videoContainer.addEventListener('touchstart', touchHandler, { passive: true });
        video.addEventListener('touchstart', touchHandler, { passive: true });

        // Store event listeners for this container
        if (!window.videoEventListeners) window.videoEventListeners = {};
        window.videoEventListeners[containerId] = [
            { element: videoContainer, type: 'click', handler: clickHandler },
            { element: video, type: 'click', handler: clickHandler },
            { element: videoContainer, type: 'touchstart', handler: touchHandler },
            { element: video, type: 'touchstart', handler: touchHandler }
        ];
    }

    updatePosition() {
        if (!this.track || this.slides.length === 0) return;
        
        const slidesPerView = this.getSlidesPerView();
        const maxIndex = Math.max(0, this.slides.length - slidesPerView);
        this.currentIndex = Math.max(0, Math.min(this.currentIndex, maxIndex));

        const slideWidth = 100 / slidesPerView;
        const translateValue = -this.currentIndex * slideWidth;

        if (this.logger) {
            this.logger.debug('Updating position:', {
                currentIndex: this.currentIndex,
                translateValue: translateValue,
                isMobile: this.isMobile,
                slidesPerView: slidesPerView,
                slideWidth: slideWidth,
                maxIndex: maxIndex,
                totalSlides: this.slides.length
            });
        }

        this.track.style.transform = `translateX(${translateValue}%)`;
    }

    updateArrowVisibility() {
        if (!this.prevButton || !this.nextButton || this.slides.length === 0) return;
        
        const totalSlides = this.slides.length;
        const slidesPerView = this.getSlidesPerView();
        const maxIndex = Math.max(0, totalSlides - slidesPerView);
        
        if (this.logger) {
            this.logger.debug('Updating arrow visibility:', {
                totalSlides,
                slidesPerView,
                maxIndex,
                currentIndex: this.currentIndex
            });
        }

        this.prevButton.style.display = 'block';
        this.nextButton.style.display = 'block';

        if (totalSlides <= slidesPerView) {
            this.prevButton.style.display = 'none';
            this.nextButton.style.display = 'none';
            if (this.logger) this.logger.debug('Hiding arrows: Not enough slides');
            return;
        }

        if (this.currentIndex === 0) {
            this.prevButton.style.display = 'none';
            if (this.logger) this.logger.debug('Hiding prev arrow: At start');
        }

        if (this.currentIndex >= maxIndex) {
            this.nextButton.style.display = 'none';
            if (this.logger) this.logger.debug('Hiding next arrow: At end');
        }
    }

    nextSlide() {
        const totalSlides = this.slides.length;
        const slidesPerView = this.getSlidesPerView();
        const maxIndex = Math.max(0, totalSlides - slidesPerView);
        
        if (this.currentIndex < maxIndex) {
            this.currentIndex = Math.min(this.currentIndex + slidesPerView, maxIndex);
        } else {
            if (this.logger) this.logger.debug('Next slide: Already at end');
            return;
        }

        if (this.logger) this.logger.debug(`Next slide: New index ${this.currentIndex}`);
        this.updatePosition();
        this.updateArrowVisibility();
    }

    prevSlide() {
        const slidesPerView = this.getSlidesPerView();
        const totalSlides = this.slides.length;
        const maxIndex = Math.max(0, totalSlides - slidesPerView);
        
        if (this.currentIndex > 0) {
            this.currentIndex = Math.max(0, this.currentIndex - slidesPerView);
        } else {
            if (this.logger) this.logger.debug('Prev slide: Already at start');
            return;
        }

        if (this.logger) this.logger.debug(`Prev slide: New index ${this.currentIndex}`);
        this.updatePosition();
        this.updateArrowVisibility();
    }
}

class TestimonialsCarousel extends BaseCarousel {
    constructor() {
        super();
        this.logger = createLogger(false, 'TestimonialsCarousel', 'debug');
        this.userType = 'default';
        this.skinCondition = 'default';
        this.beforeAfterDataUrl = '/assets/lp1-before-after-data.json';
        window.beforeAfterData = window.beforeAfterData || null;
        this.logger.info('TestimonialsCarousel constructor');
    }

    connectedCallback() {
        this.logger.info('TestimonialsCarousel connected');
        this.beforeAfterDataUrl = this.getAttribute('data-url') || this.beforeAfterDataUrl;

        this.handleSolutionsShown = (event) => {
            const { person, skinCondition } = event.detail;
            this.logger.info('Solutions shown, updating testimonials with:', { person, skinCondition });
            this.userType = person || 'default';
            this.skinCondition = skinCondition || 'default';
            if (window.beforeAfterData) {
                this.renderSlides();
            } else {
                this.loadBeforeAfterData();
            }
        };
        document.addEventListener('solutionsShown', this.handleSolutionsShown);

        super.connectedCallback();

        this.loadBeforeAfterData();
    }
    
    disconnectedCallback() {
        super.disconnectedCallback();
        this.logger.info('TestimonialsCarousel disconnected');
    }

    async loadBeforeAfterData() {
        if (window.beforeAfterData) {
            this.logger.info('Using cached before/after data');
            this.renderSlides();
            return;
        }

        this.logger.info(`Loading before/after data from ${this.beforeAfterDataUrl}`);
        try {
            const response = await fetch(this.beforeAfterDataUrl);
            if (!response.ok) {
                throw new Error(`Failed to load before/after data: ${response.statusText}`);
            }
            const text = await response.text();
            const jsonText = text.trim();
            const data = JSON.parse(jsonText);

            this.logger.info('Before/after data loaded successfully');
            window.beforeAfterData = data;
            this.renderSlides();
        } catch (error) {
            this.logger.error('Error loading or parsing before/after data:', error);
            window.beforeAfterData = {
                default: {
                    mobileSlides: 1,
                    desktopSlides: 1,
                    slides: []
                }
            };
            this.logger.warn('Using fallback empty data structure.');
            this.renderSlides();
        }
    }

    getBeforeAfterDataByQuizAnswers() {
        this.logger.info('Getting before/after data for:', {
            userType: this.userType,
            skinCondition: this.skinCondition
        });

        if (!window.beforeAfterData) {
            this.logger.warn('Before/after data not loaded, returning default empty structure.');
            return { mobileSlides: 1, desktopSlides: 1, slides: [] };
        }

        const userTypeMap = {
            'me': 'Me',
            'child': 'My Child (0-12)',
            'teen': 'My Tween / Teen (12+)'
        };

        const skinConditionMap = {
            'eczema': 'Eczema-Prone',
            'psoriasis': 'Psoriasis-Prone',
            'rosacea': 'Rosacea-Prone',
            'acne': 'Acne-Prone',
            'not_sure': 'Not Sure'
        };

        const mappedUserType = userTypeMap[this.userType] || 'default';
        const mappedSkinCondition = skinConditionMap[this.skinCondition] || 'default';

        let dataSet = window.beforeAfterData.default || { mobileSlides: 1, desktopSlides: 1, slides: [] };

        if (mappedUserType !== 'default' && window.beforeAfterData[mappedUserType]) {
            if (mappedSkinCondition !== 'default' && window.beforeAfterData[mappedUserType][mappedSkinCondition]) {
                if (window.beforeAfterData[mappedUserType][mappedSkinCondition].slides?.length > 0) {
                    dataSet = window.beforeAfterData[mappedUserType][mappedSkinCondition];
                    this.logger.info(`Found specific data for ${mappedUserType} -> ${mappedSkinCondition}`);
                } else {
                    this.logger.info(`No slides found for ${mappedUserType} -> ${mappedSkinCondition}, using default.`);
                }
            } else {
                this.logger.info(`Skin condition '${mappedSkinCondition}' not found for user type '${mappedUserType}', using default.`);
            }
        } else if (mappedUserType !== 'default') {
            this.logger.info(`User type '${mappedUserType}' not found, using global default.`);
        } else {
            this.logger.info('Using global default data.');
        }

        return {
            mobileSlides: dataSet?.mobileSlides || 1,
            desktopSlides: dataSet?.desktopSlides || 1,
            slides: dataSet?.slides || [],
            bg: dataSet?.bg || ''
        };
    }

    renderSlides() {
        this.logger.info('Rendering slides...');
        const data = this.getBeforeAfterDataByQuizAnswers();

        if (!data) {
            this.logger.error('No data available to render slides.');
            return;
        }

        this.logger.debug('Using data:', data);

        this.setAttribute('mobile-slides', data.mobileSlides);
        this.setAttribute('desktop-slides', data.desktopSlides);

        if (data.bg) {
            this.classList.add(data.bg);
            this.logger.debug('Applied background class:', data.bg);
        }

        if (!this.track) {
            this.track = this.querySelector('[data-carousel-track]');
            if (!this.track) {
                this.logger.error('Cannot render slides: Carousel track not found.');
                return;
            }
        }

        this.track.innerHTML = '';
        this.slides = [];

        if (!data.slides || data.slides.length === 0) {
            this.logger.warn('No slides found in the data for the current selection.');
            this.track.innerHTML = '<p class="text-center p-4 text-gray-500">No relevant before & after examples available.</p>';
            this.currentIndex = 0;
            this.initialize();
            return;
        }

        data.slides.forEach((slideData, index) => {
            const slideElement = document.createElement('div');
            slideElement.className = 'carousel-slide flex-none justify-center';

            let innerHTML = '';
            const uniqueId = `ba-slide-${Date.now()}-${index}`;

            if (slideData.type === 'image' && slideData.src) {
                innerHTML = `
                    <div class="p-4 md:p-6">
                        <img
                            src="${slideData.src}"
                            alt="Before/After Example"
                            class="w-full h-auto rounded object-contain max-h-[420px]"
                            loading="lazy"
                        >
                    </div>
                `;
            } else if (slideData.type === 'video' && slideData.src) {
                const mutedIcon = `<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clip-rule="evenodd"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"></path></svg>`;
                const unmutedIcon = `<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"></path></svg>`;

                const canMute = slideData.can_mute || false;
                const muteButton = canMute ? `
                    <button class="absolute bottom-2 right-2 md:bottom-4 md:right-4 bg-black/50 text-white p-1 md:p-2 rounded-full hover:bg-black/70 transition-colors opacity-80 hover:opacity-100 z-10" data-mute-toggle title="Toggle Sound">
                        ${mutedIcon}
                    </button>
                ` : '';

                innerHTML = `
                    <div class="p-4 md:p-6">
                        <div class="video-autoplay-wrapper max-w-[420px] max-h-[420px] mx-auto rounded-[18px] md:rounded-[24px] overflow-hidden cursor-pointer relative" data-video-container="${uniqueId}">
                            <video
                                src="${slideData.src}"
                                autoplay
                                loop
                                muted
                                playsinline
                                preload="metadata"
                                class="w-full h-full object-cover bg-gray-200"
                                data-video-autoplay
                            ></video>
                            ${muteButton}
                        </div>
                    </div>
                `;
            } else {
                this.logger.warn('Unknown or invalid slide type/src:', slideData);
                innerHTML = '<div class="p-6 text-center text-red-500">Invalid slide data</div>';
            }

            slideElement.innerHTML = innerHTML;
            this.track.appendChild(slideElement);
        });

        this.currentIndex = 0;
        this.initialize();

        this.logger.info(`Rendered ${this.slides.length} slides.`);
    }
}

class ReviewsCarousel extends BaseCarousel {
    constructor() {
        super();
        this.logger = createLogger(false, 'ReviewsCarousel', 'debug');
        this.userType = 'default';
        this.skinCondition = 'default';
        this.reviewsDataUrl = '/assets/reviews-data.json';
        window.reviewsData = window.reviewsData || null;
        this.logger.info('ReviewsCarousel constructor');
    }

    connectedCallback() {
        this.logger.info('ReviewsCarousel connected');
        this.reviewsDataUrl = this.getAttribute('data-reviews-url') || this.reviewsDataUrl;

        this.handleSolutionsShown = (event) => {
            const { person, skinCondition } = event.detail;
            this.logger.info('Solutions shown, updating reviews with:', { person, skinCondition });
            this.userType = person || 'default';
            this.skinCondition = skinCondition || 'default';
            if (window.reviewsData) {
                this.renderReviews();
            } else {
                this.loadReviewsData();
            }
        };
        document.addEventListener('solutionsShown', this.handleSolutionsShown);

        super.connectedCallback();

        this.loadReviewsData();
    }
    
    disconnectedCallback() {
        super.disconnectedCallback();
        this.logger.info('ReviewsCarousel disconnected');
    }

    async loadReviewsData() {
        if (window.reviewsData) {
            this.logger.info('Using cached reviews data');
            this.renderReviews();
            return;
        }

        this.logger.info(`Loading reviews data from ${this.reviewsDataUrl}`);
        try {
            const response = await fetch(this.reviewsDataUrl);
            if (!response.ok) {
                throw new Error(`Failed to load reviews data: ${response.statusText}`);
            }
            const text = await response.text();
            const jsonText = text.trim();
            const data = JSON.parse(jsonText);

            this.logger.info('Reviews data loaded successfully');
            window.reviewsData = data;
            this.renderReviews();
        } catch (error) {
            this.logger.error('Error loading or parsing reviews data:', error);
            window.reviewsData = {
                default: [
                    {
                        text: "Default review: We're happy to help! Contact us for personalized recommendations.",
                        author: "Bio-First Team",
                        stars: 5
                    }
                ]
            };
            this.logger.warn('Using fallback review data structure.');
            this.renderReviews();
        }
    }

    getReviewsByQuizAnswers() {
        this.logger.info('Getting reviews for:', {
            userType: this.userType,
            skinCondition: this.skinCondition
        });

        if (!window.reviewsData) {
            this.logger.warn('Reviews data not loaded, returning default fallback review.');
            return [{ text: "Loading reviews...", author: "", stars: 0 }];
        }

        const userTypeMap = {
            'me': 'Me',
            'child': 'My Child (0-12)',
            'teen': 'My Tween / Teen (12+)'
        };
        const skinConditionMap = {
            'eczema': 'Eczema-Prone',
            'psoriasis': 'Psoriasis-Prone',
            'rosacea': 'Rosacea-Prone',
            'acne': 'Acne-Prone',
            'not_sure': 'Not Sure'
        };

        const mappedUserType = userTypeMap[this.userType] || 'default';
        const mappedSkinCondition = skinConditionMap[this.skinCondition] || 'default';

        let reviews = window.reviewsData.default || [];

        if (mappedUserType !== 'default' && window.reviewsData[mappedUserType]) {
            if (mappedSkinCondition !== 'default' && window.reviewsData[mappedUserType][mappedSkinCondition]) {
                if (Array.isArray(window.reviewsData[mappedUserType][mappedSkinCondition]) && window.reviewsData[mappedUserType][mappedSkinCondition].length > 0) {
                    reviews = window.reviewsData[mappedUserType][mappedSkinCondition];
                    this.logger.info(`Found ${reviews.length} specific reviews for ${mappedUserType} -> ${mappedSkinCondition}`);
                } else {
                    this.logger.info(`No specific reviews array found for ${mappedUserType} -> ${mappedSkinCondition}, using default.`);
                }
            } else {
                this.logger.info(`Skin condition '${mappedSkinCondition}' not found or empty for user type '${mappedUserType}', using default.`);
            }
        } else if (mappedUserType !== 'default') {
            this.logger.info(`User type '${mappedUserType}' not found, using global default.`);
        } else {
            this.logger.info('Using global default reviews.');
        }

        return Array.isArray(reviews) ? reviews : (window.reviewsData.default || []);
    }

    renderReviews() {
        this.logger.info('Rendering reviews...');
        const reviews = this.getReviewsByQuizAnswers();

        if (!this.track) {
            this.track = this.querySelector('[data-carousel-track]');
            if (!this.track) {
                this.logger.error('Cannot render reviews: Carousel track not found.');
                return;
            }
        }

        this.track.innerHTML = '';
        this.slides = [];

        if (!reviews || reviews.length === 0) {
            this.logger.warn('No reviews found for the current selection.');
            this.track.innerHTML = '<p class="text-center p-4 text-gray-500">No relevant reviews available.</p>';
            this.currentIndex = 0;
            this.initialize();
            return;
        }

        reviews.forEach(review => {
            const slide = document.createElement('div');
            slide.className = 'review-slide flex-none flex items-center sm:items-start';

            const starsHTML = Array(5).fill(0).map((_, i) => `
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 md:h-5 md:w-5" viewBox="0 0 20 20" fill="#FFA602">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            `).join('');

            slide.innerHTML = `
                <div class="flex flex-col gap-4 p-4 py-0 md:p-6 h-auto items-center sm:items-start">
                    <div class="flex justify-center md:justify-start text-[#FFA602] mb-2">
                        ${starsHTML}
                    </div>
                    <div class="text-[#6D5C4B] text-sm md:text-base text-center md:text-left flex-grow italic">
                        ${review.text}
                    </div>
                    <p class="text-[#6D5C4B] font-medium mt-2 text-center md:text-right">— ${review.author}</p>
                </div>
            `;

            this.track.appendChild(slide);
        });

        this.currentIndex = 0;
        this.initialize();

        this.logger.info(`Rendered ${this.slides.length} reviews.`);
    }
}

class BfVideo extends HTMLElement {
    constructor() {
        super();
        this.logger = console;
    }

    connectedCallback() {
        this.logger.info('Video element connected');
        this.initialize();
        
        // Handle solutionsShown event
        this.handleSolutionsShown = () => {
            if (this.logger) this.logger.debug('Solutions shown, reinitializing video');
            // Small delay to ensure DOM is ready
            setTimeout(() => {
                this.initialize();
            }, 100);
        };
        document.addEventListener('solutionsShown', this.handleSolutionsShown);
    }

    disconnectedCallback() {
        document.removeEventListener('solutionsShown', this.handleSolutionsShown);
    }

    initialize() {
        const videoContainer = this.querySelector('[data-video-container]');
        if (!videoContainer) return;

        const video = videoContainer.querySelector('video');
        if (!video) return;

        const muteToggle = videoContainer.querySelector('[data-mute-toggle]');
        
        // Remove any existing observers and event listeners
        if (window.videoObservers && window.videoObservers[this.id]) {
            window.videoObservers[this.id].disconnect();
        }
        if (window.videoEventListeners && window.videoEventListeners[this.id]) {
            window.videoEventListeners[this.id].forEach(listener => {
                listener.element.removeEventListener(listener.type, listener.handler);
            });
        }

        // Reset video state
        video.pause();
        video.currentTime = 0;
        video.muted = true;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    if (video.muted) {
                        const playPromise = video.play();
                        if (playPromise !== undefined) {
                            playPromise.catch(err => {
                                if (this.logger) this.logger.debug('Autoplay failed:', err);
                                observer.unobserve(videoContainer);
                                setTimeout(() => observer.observe(videoContainer), 100);
                            });
                        }
                    }
                } else {
                    video.pause();
                }
            });
        }, {
            threshold: [0.1, 0.5, 0.9],
            rootMargin: '50px'
        });

        observer.observe(videoContainer);

        // Store observer
        if (!window.videoObservers) window.videoObservers = {};
        window.videoObservers[this.id] = observer;

        // Create click handler
        const clickHandler = function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            if (video.paused) {
                video.play().catch(err => {
                    if (this.logger) this.logger.debug('Play failed:', err);
                });
            } else {
                video.pause();
            }
        };

        // Create touch handler
        const touchHandler = function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            if (video.paused && video.muted) {
                video.play().catch(err => {
                    if (this.logger) this.logger.debug('Touch play failed:', err);
                });
            }
        };

        // Add event listeners
        videoContainer.addEventListener('click', clickHandler);
        video.addEventListener('click', clickHandler);
        videoContainer.addEventListener('touchstart', touchHandler, { passive: true });
        video.addEventListener('touchstart', touchHandler, { passive: true });

        // Store event listeners
        if (!window.videoEventListeners) window.videoEventListeners = {};
        window.videoEventListeners[this.id] = [
            { element: videoContainer, type: 'click', handler: clickHandler },
            { element: video, type: 'click', handler: clickHandler },
            { element: videoContainer, type: 'touchstart', handler: touchHandler },
            { element: video, type: 'touchstart', handler: touchHandler }
        ];

        // Initialize mute button
        if (muteToggle) {
            const updateMuteButton = () => {
                const isMuted = video.muted;
                const svg = muteToggle.querySelector('svg');
                const paths = svg.querySelectorAll('path');
                
                if (isMuted) {
                    paths[1].style.display = 'block';
                } else {
                    paths[1].style.display = 'none';
                }
            };

            const muteHandler = function(e) {
                e.stopPropagation();
                video.muted = !video.muted;
                updateMuteButton();
            };

            muteToggle.addEventListener('click', muteHandler);
            window.videoEventListeners[this.id].push({
                element: muteToggle,
                type: 'click',
                handler: muteHandler
            });

            updateMuteButton();
        }
    }
}

export function initializeCustomElements() {
    logger.info('Initializing custom elements');
    
    if (!customElements.get('bf-base-carousel')) {
        customElements.define('bf-base-carousel', BaseCarousel);
        logger.info('Registered bf-base-carousel');
    }
    
    if (!customElements.get('bf-testimonials-carousel')) {
        customElements.define('bf-testimonials-carousel', TestimonialsCarousel);
        logger.info('Registered bf-testimonials-carousel');
    }
    if (!customElements.get('bf-reviews-carousel')) {
        customElements.define('bf-reviews-carousel', ReviewsCarousel);
        logger.info('Registered bf-reviews-carousel');
    }

    if (customElements.get('bf-testimonials-carousel') && customElements.get('bf-reviews-carousel')) {
        logger.info('All expected carousel elements are registered.');
    } else {
        logger.warn('Some carousel elements might still be unregistered.');
    }

    if (!customElements.get('bf-video')) {
        customElements.define('bf-video', BfVideo);
    }
} 